import useAppointmentEvents from '@hooks/useAppointmentEvents';
import { Appointment } from 'vl-common/src/schemas/shared/appointment';
import { getMostRecentEventStatusMessage } from '@hooks/useAppointmentStatus';

export type AudioCallStatus =
  | 'idle'
  | 'initiated'
  | 'ringing'
  | 'answered'
  | 'in-progress'
  | 'completed'
  | 'failed'
  | 'no-answer'
  | 'busy'
  | 'canceled';

type VoiceCallEvent = { CallStatus: AudioCallStatus; ErrorMessage: string | undefined };

export const CallStatusTextEnum = {
  IDLE: 'Waiting to call...',
  INITIATED: 'Preparing call...',
  RINGING: 'Calling patient...',
  ANSWERED: 'Patient answered',
  IN_PROGRESS: 'Call in progress...',
  COMPLETED: 'Call completed',
  INVALID_NUMBER: 'Invalid phone number',
  FAILED: 'Call failed or number unreachable',
  NO_ANSWER: 'Patient did not answer',
  BUSY: 'Patient is busy',
  CANCELED: 'Call canceled before answer',
  UNKNOWN: 'Call status unknown',
  INTERNATIONAL: 'International call not allowed'
} as const;

const mapAudioCallStatusToText = (voiceCallEvent: VoiceCallEvent) => {
  switch (voiceCallEvent?.CallStatus || 'idle') {
    case 'idle':
      return CallStatusTextEnum.IDLE;
    case 'initiated':
      return CallStatusTextEnum.INITIATED;
    case 'ringing':
      return CallStatusTextEnum.RINGING;
    case 'answered':
      return CallStatusTextEnum.ANSWERED;
    case 'in-progress':
      return CallStatusTextEnum.IN_PROGRESS;
    case 'completed':
      return CallStatusTextEnum.COMPLETED;
    case 'no-answer':
      return CallStatusTextEnum.NO_ANSWER;
    case 'busy':
      return CallStatusTextEnum.BUSY;
    case 'canceled':
      return CallStatusTextEnum.CANCELED;
    case 'failed':
      if (voiceCallEvent?.ErrorMessage) {
        const errorMessage = voiceCallEvent.ErrorMessage.charAt(0).toUpperCase() + voiceCallEvent.ErrorMessage.slice(1);
        return errorMessage.includes('No International Permission') ? CallStatusTextEnum.INTERNATIONAL : errorMessage;
      }
      return CallStatusTextEnum.FAILED;
    default:
      return CallStatusTextEnum.UNKNOWN;
  }
};

export const useAudioCallStatus = (appointment: Appointment, currentCallSid: string) => {
  const { events } = useAppointmentEvents(appointment);
  const filteredByCallSidEvents = currentCallSid
    ? events.filter((e) => {
        try {
          const eventString = e?.message?.toString()?.split('&')?.[1] || '{}';
          const event = JSON.parse(eventString);
          return event?.ParentCallSid === currentCallSid;
        } catch (error) {
          console.error('Failed to parse event JSON:', error, e);
          return false;
        }
      })
    : [];
  const mostRecentVoiceCallEvent = getMostRecentEventStatusMessage(filteredByCallSidEvents, 'voiceCallback&');

  return {
    audioCallStatus: mostRecentVoiceCallEvent?.CallStatus || 'idle',
    audioCallStatusText: mapAudioCallStatusToText(mostRecentVoiceCallEvent)
  };
};
